// Plain-English formatting for football markets. The engine speaks in codes —
// canonical_code ("total_goals_over_2_5", "asian_handicap_home_minus_0_5",
// "match_winner_home", "btts_yes") and market+selection pairs ("total_goals_2_5"
// + "over", "match_winner" + "home"). Users must see natural language only.
//
// Two entry points:
//   formatCanonical(code)            — one canonical_code string → English
//   formatLeg(market, selection)     — a (market, selection) pair → English
// Both fall back to readable Title Case so an unmapped code never leaks a
// raw underscore to the user.

// "2_5" → "2.5", "0_5" → "0.5", "1" → "1"
function decodeLine(token: string): string {
  return (token || "").replace(/_/g, ".");
}

/** Optional fixture context. When supplied, side tokens ("home"/"Home Win"/"1")
 *  resolve to the ACTUAL team name in the rendered label — so users read
 *  "Sandefjord" instead of the generic "Home win". Without context the
 *  formatter falls back to the side phrasing (no fabrication). */
export interface LegContext {
  home_team?: string | null;
  away_team?: string | null;
}

/** Map any side-token form ("home" / "Home Win" / "1" / "h") to a canonical
 *  "home" | "away" | "draw" | null. Case-insensitive. Strips a trailing
 *  " win"/" team" suffix so both "Home" and "Home Win" classify. */
function normaliseSideToken(sel: string): "home" | "away" | "draw" | null {
  const s = String(sel ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+(win|team)$/i, "")
    .trim();
  if (s === "home" || s === "h" || s === "1") return "home";
  if (s === "away" || s === "a" || s === "2") return "away";
  if (s === "draw" || s === "tie" || s === "x") return "draw";
  return null;
}

// Only underscores are code separators — hyphens are kept so human text
// like correct-score lines ("2-1") and hyphenated names ("Rot-Weiss") survive.
function titleCase(s: string): string {
  return (s || "")
    .replace(/_+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

const SIDE: Record<string, string> = { home: "Home", away: "Away", draw: "Draw" };

// Asian-handicap line + side, e.g. ("home","minus","0_5") → "Home -0.5".
function ahPhrase(side: string, sign: "plus" | "minus", line: string): string {
  const s = SIDE[side] ?? titleCase(side);
  const num = `${sign === "minus" ? "-" : "+"}${decodeLine(line)}`;
  return `${s} ${num}`;
}

// One canonical_code → English.
export function formatCanonical(code: string | null | undefined): string {
  const c = String(code ?? "")
    .trim()
    .toLowerCase();
  if (!c) return "—";
  let m: RegExpMatchArray | null;

  if ((m = c.match(/^match_winner_(home|draw|away)$/))) {
    return m[1] === "draw" ? "Draw" : `${SIDE[m[1]!]} win`;
  }
  if ((m = c.match(/^total_goals_(over|under)_([0-9_]+)$/))) {
    return `${m[1] === "over" ? "Over" : "Under"} ${decodeLine(m[2]!)} goals`;
  }
  if ((m = c.match(/^asian_handicap_(home|away)_(minus|plus)_([0-9_]+)$/))) {
    return `${ahPhrase(m[1]!, m[2] as "plus" | "minus", m[3]!)} (Asian handicap)`;
  }
  if ((m = c.match(/^btts_(yes|no)$/))) {
    return `Both teams to score: ${m[1] === "yes" ? "Yes" : "No"}`;
  }
  if (c === "match_winner" || c === "1x2") return "Match result";
  // Coarse market_type buckets used by the signal-card surface
  // ("football_match_result", "football_btts", "football_over_under",
  // "football_custom"). Map to a clean category, never a raw token.
  if ((m = c.match(/^(?:football|soccer)_(.+)$/))) {
    const kind = m[1]!;
    if (kind === "match_result" || kind === "match_winner" || kind === "1x2") return "Match result";
    if (kind === "btts" || kind === "both_teams_to_score") return "Both teams to score";
    if (kind === "over_under" || kind === "totals" || kind === "total_goals")
      return "Over/Under goals";
    if (kind === "asian_handicap" || kind === "handicap") return "Asian handicap";
    if (kind === "custom" || kind === "other") return "Match market";
    return titleCase(kind);
  }
  return titleCase(c);
}

// A (market, selection) pair → English. selection may be "over"/"home"/"yes"
// or already-human ("Over 2.5", a team name, or "Home Win"). When LegContext
// is supplied, side tokens resolve to the actual team name — so a 1X2 home
// pick reads "Sandefjord" instead of "Home win".
export function formatLeg(
  market: string | null | undefined,
  selection: string | null | undefined,
  ctx?: LegContext,
): string {
  const mk = String(market ?? "")
    .trim()
    .toLowerCase();
  const sel = String(selection ?? "").trim();
  const selLower = sel.toLowerCase();
  // Normalised side token (handles "Home Win", "home", "1", "h" all alike).
  // Used by the match-result + handicap branches below.
  const side = normaliseSideToken(sel);
  const sideToTeam = (s: "home" | "away" | "draw" | null): string | null => {
    if (!ctx) return null;
    if (s === "home" && ctx.home_team) return ctx.home_team;
    if (s === "away" && ctx.away_team) return ctx.away_team;
    return null;
  };

  let m: RegExpMatchArray | null;

  // Asian handicap with the side in the SELECTION ("asian_handicap_minus_0_5" + "home").
  // Checked before the full-canonical detector (whose AH form carries the side
  // in the code itself), so this pair form isn't mis-routed.
  if ((m = mk.match(/^asian_handicap_(minus|plus)_([0-9_]+)$/))) {
    const sideKey = side === "home" || side === "away" ? side : "home";
    const teamName = sideToTeam(sideKey);
    const sign = m[1] === "minus" ? "-" : "+";
    if (teamName) return `${teamName} ${sign}${decodeLine(m[2]!)} (Asian handicap)`;
    return `${ahPhrase(sideKey, m[1] as "plus" | "minus", m[2]!)} (Asian handicap)`;
  }

  // If the "market" is actually a full canonical code (side baked in), format it.
  if (
    /_(home|away|draw|over|under|yes|no)(_|$)/.test(mk) ||
    /^asian_handicap_(home|away)_/.test(mk)
  ) {
    const base = formatCanonical(mk);
    if (base !== titleCase(mk)) return base;
  }

  // Match result family — accepts code ("match_winner") and already-human
  // ("Match Winner", "Live Match Winner") market labels.
  //   • If team context is provided AND the selection is a side token
  //     (home / "Home Win" / 1 / h …) → render the ACTUAL team name. This is
  //     what users want to see, not the generic "Home win".
  //   • If no context, fall back to "Home win"/"Away win"/"Draw" — but using
  //     the case-insensitive normaliser so "Home Win" no longer leaks raw.
  //   • If the selection is already a team name, pass it through unchanged.
  if (
    mk === "match_winner" ||
    mk === "match_result" ||
    mk === "1x2" ||
    mk === "result" ||
    mk === "match result" ||
    mk === "match winner" ||
    mk === "live match winner" ||
    mk === "draw"
  ) {
    if (side === "home") return sideToTeam("home") ?? "Home win";
    if (side === "away") return sideToTeam("away") ?? "Away win";
    if (side === "draw") return "Draw";
    return sel || "Match result";
  }
  // Totals: market "total_goals_2_5" / "total_goals" / "ou", selection over/under
  if ((m = mk.match(/^total_goals(?:_([0-9_]+))?$/)) || mk === "ou" || mk === "total_goals") {
    const line = m && m[1] ? decodeLine(m[1]) : (extractLine(sel) ?? "2.5");
    const dir = selLower.startsWith("over")
      ? "Over"
      : selLower.startsWith("under")
        ? "Under"
        : titleCase(sel);
    return `${dir} ${line} goals`;
  }
  // BTTS
  if (mk === "btts" || mk.startsWith("btts")) {
    return `Both teams to score: ${selLower === "yes" ? "Yes" : selLower === "no" ? "No" : titleCase(sel)}`;
  }
  if (mk === "double_chance" || mk === "double chance") {
    const dc = selLower.match(/^(home|away)_(?:or_)?(draw|home|away)$/);
    if (dc) {
      const first = dc[1] === "home" ? (ctx?.home_team ?? "Home") : (ctx?.away_team ?? "Away");
      const second =
        dc[2] === "draw"
          ? "draw"
          : dc[2] === "home"
            ? (ctx?.home_team ?? "Home")
            : (ctx?.away_team ?? "Away");
      return `${first} or ${second}`;
    }
  }
  // Asian handicap: market "asian_handicap_minus_0_5", selection home/away
  // (dead branch — same shape handled at the top of the function — kept here
  // as a belt-and-braces fallback for unusual input ordering).
  if ((m = mk.match(/^asian_handicap_(minus|plus)_([0-9_]+)$/))) {
    const sideKey = side === "home" || side === "away" ? side : "home";
    const teamName = sideToTeam(sideKey);
    const sign = m[1] === "minus" ? "-" : "+";
    if (teamName) return `${teamName} ${sign}${decodeLine(m[2]!)} (Asian handicap)`;
    return `${ahPhrase(sideKey, m[1] as "plus" | "minus", m[2]!)} (Asian handicap)`;
  }
  if (mk === "asian_handicap" || mk === "ah") {
    const ah = selLower.match(/^(home|away)_(plus|minus)_([0-9_]+)$/);
    if (ah) {
      const team = ah[1] === "home" ? (ctx?.home_team ?? "Home") : (ctx?.away_team ?? "Away");
      const sign = ah[2] === "minus" ? "-" : "+";
      return `${team} ${sign}${decodeLine(ah[3]!)} (Asian handicap)`;
    }
    return sel ? `${titleCase(sel)} (Asian handicap)` : "Asian handicap";
  }

  // Corners / cards totals — selection encodes the line as "over_9_5" / "under_3_5".
  if (mk === "over_under_corners" || mk === "corners" || mk === "total_corners") {
    const line = (sel.match(/(\d+(?:_\d+)?)/)?.[1] ?? "").replace("_", ".");
    const dir = selLower.startsWith("over")
      ? "Over"
      : selLower.startsWith("under")
        ? "Under"
        : titleCase(sel);
    return line ? `${dir} ${line} corners` : `${dir} corners`;
  }
  if (mk === "over_under_cards" || mk === "cards" || mk === "total_cards") {
    const line = (sel.match(/(\d+(?:_\d+)?)/)?.[1] ?? "").replace("_", ".");
    const dir = selLower.startsWith("over")
      ? "Over"
      : selLower.startsWith("under")
        ? "Under"
        : titleCase(sel);
    return line ? `${dir} ${line} cards` : `${dir} cards`;
  }
  // Anytime goalscorer — the selection is the player's name.
  if (mk === "anytime_goalscorer" || mk === "goalscorer" || mk === "anytime_scorer") {
    return sel ? `${sel} to score` : "Anytime goalscorer";
  }

  // Fallback: readable market + selection, never raw underscores.
  const mkLabel = titleCase(mk);
  return sel
    ? `${mkLabel}: ${titleCase(sel) === sel ? sel : titleCase(sel)}`
    : mkLabel || sel || "—";
}

function extractLine(s: string): string | null {
  const m = String(s || "").match(/([0-9]+)(?:[._]([0-9]+))?/);
  if (!m) return null;
  return m[2] ? `${m[1]}.${m[2]}` : m[1]!;
}

// Category-only label, for two-column grids/tables (market column).
export function marketLabel(market: string | null | undefined): string {
  const mk = String(market ?? "")
    .trim()
    .toLowerCase();
  if (!mk) return "—";
  if (mk === "match_winner" || mk === "1x2" || mk === "result") return "Match result";
  if (/^total_goals/.test(mk) || mk === "ou") return "Total goals";
  if (/^asian_handicap/.test(mk) || mk === "ah") return "Asian handicap";
  if (/^btts/.test(mk)) return "Both teams to score";
  if (mk === "over_under_corners" || mk === "corners" || mk === "total_corners")
    return "Total corners";
  if (mk === "over_under_cards" || mk === "cards" || mk === "total_cards") return "Total cards";
  if (mk === "anytime_goalscorer" || mk === "goalscorer" || mk === "anytime_scorer")
    return "Anytime goalscorer";
  return titleCase(mk);
}

// Human selection only, for the selection column of a grid/table.
// Pass `ctx` ({home_team, away_team}) to resolve 1X2/AH side tokens to the
// actual team name — users read "Sandefjord" instead of "Home win".
export function selectionLabel(
  market: string | null | undefined,
  selection: string | null | undefined,
  ctx?: LegContext,
): string {
  const full = formatLeg(market, selection, ctx);
  const cat = marketLabel(market);
  // formatLeg returns "Category: value" for some markets — strip the prefix so
  // the selection column isn't redundant with the market column.
  if (full.startsWith(`${cat}: `)) return full.slice(cat.length + 2);
  return full;
}
